import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml, SafeUrl } from '@angular/platform-browser';
import {
  ButtonDirective,
  FormControlDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
} from '@coreui/angular';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { FormsModule } from '@angular/forms';
import { ToastService } from '../../../app/services/toast.service';

export interface CredentialPayload {
  file: File | null;
  fullName: string;
}

@Component({
  selector: 'app-credentials-one',
  standalone: true,
  imports: [
    ButtonDirective,
    FormControlDirective,
    ModalComponent,
    ModalFooterComponent,
    ModalBodyComponent,
    ImageCropperComponent,
    FormsModule,
    CommonModule,
  ],
  templateUrl: './credentials-one.component.html',
  styleUrl: './credentials-one.component.scss',
})
export class CredentialsOneComponent implements OnChanges, OnDestroy {
  private avatarLoadTimeout: any;

  @Input() selectTextOrImage: 1 | 2 = 1;
  @Input() textCampaign = '';
  @Input() imageCampaign = '';
  @Input() mainBackground = '';
  @Input() diseingMobile = false;

  @Input() visibleLogoSection = true;
  @Input() image = '';
  @Input() fullName = '';
  @Input() title = '';
  safeTitle: SafeHtml = '';
  @Input() formBackground = 'transparent';
  @Input() buttonUploadProfileBackground = '#000000';
  @Input() buttonGenerateCredentialBackground = '#000000';
  @Input() buttonUploadStyle = 'uppercase';
  @Input() buttonGenerateStyle = 'uppercase';
  @Input() design: string = '1';

  safeTextCampaign: SafeHtml = '';

  @Input() loading = false;
  @Input() activeButton = true;
  @Input() inputFullNameDisabled: boolean = false;

  @ViewChild('credentialsOne', { static: true })
  credentialsOne!: ElementRef<HTMLDivElement>;
  @ViewChild(ImageCropperComponent)
  imageCropperComponent!: ImageCropperComponent;

  @Output() generate = new EventEmitter<CredentialPayload>();

  defaultCampaignImage = '../assets/img/noimage_landing.png';
  imageError = false;
  imageLoaded = false;
  avatarLoaded = false;

  public imageBase64ForCropper: string = '';
  public zoom: number = 1;
  public modal: boolean = false;
  public croppedImage: SafeUrl = '';
  public selectedFile: File | null = null;
  public fullNameValue = '';

  private toastService = inject(ToastService);

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.setCssVariable('--form-background', this.formBackground);
    this.updateButtonStyle('upload', this.buttonUploadStyle);
    this.updateButtonStyle('generate', this.buttonGenerateStyle);
    this.safeTitle = this.sanitizer.bypassSecurityTrustHtml(this.title);
    this.safeTextCampaign = this.sanitizer.bypassSecurityTrustHtml(
      this.textCampaign,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['imageCampaign']) {
      this.imageError = false;
      this.imageLoaded = false;
    }
    if (changes['image']) {
      clearTimeout(this.avatarLoadTimeout);
      this.avatarLoaded = false;
    }
    if (changes['title']) {
      this.safeTitle = this.sanitizer.bypassSecurityTrustHtml(this.title);
    }
    if (changes['textCampaign']) {
      this.safeTextCampaign = this.sanitizer.bypassSecurityTrustHtml(
        this.textCampaign,
      );
    }
    if (changes['formBackground']) {
      this.setCssVariable('--form-background', this.formBackground);
    }
    if (changes['buttonUploadProfileBackground']) {
      this.setCssVariable(
        '--button-upload-profile-background',
        this.buttonUploadProfileBackground,
      );
    }
    if (changes['buttonGenerateCredentialBackground']) {
      this.setCssVariable(
        '--button-generate-credential-background',
        this.buttonGenerateCredentialBackground,
      );
    }
    if (changes['buttonUploadStyle']) {
      this.updateButtonStyle('upload', this.buttonUploadStyle);
    }
    if (changes['buttonGenerateStyle']) {
      this.updateButtonStyle('generate', this.buttonGenerateStyle);
    }
  }

  updateButtonStyle(buttonType: 'upload' | 'generate', styleValue: string) {
    const parts = (styleValue || 'none').split('-');
    const caseValues = ['uppercase', 'lowercase', 'capitalize'];

    const isBold = parts.includes('bold');
    const isItalic = parts.includes('italic');
    const isUnderline = parts.includes('underline');
    const caseVal = parts.find((p) => caseValues.includes(p));

    const textTransform = caseVal ?? 'none';
    const fontWeight = isBold ? '700' : '500';
    const fontStyle = isItalic ? 'italic' : 'normal';
    const textDecoration = isUnderline ? 'underline' : 'none';

    this.setCssVariable(`--button-${buttonType}-text-transform`, textTransform);
    this.setCssVariable(`--button-${buttonType}-font-weight`, fontWeight);
    this.setCssVariable(`--button-${buttonType}-font-style`, fontStyle);
    this.setCssVariable(
      `--button-${buttonType}-text-decoration`,
      textDecoration,
    );
  }

  setCssVariable(variable: string, value: string) {
    this.credentialsOne.nativeElement.style.setProperty(variable, value);
  }

  openFileSelector() {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.click();
  }

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.zoom = 1;
      this.resizeImageBeforeCrop(file).then((base64) => {
        this.imageBase64ForCropper = base64;
        this.modal = true;
      });
      setTimeout(() => {
        input.value = '';
      }, 0);
    } else {
      this.modal = false;
    }
  }

  private resizeImageBeforeCrop(file: File, maxSize = 1200): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const { width, height } = img;
          let targetW = width;
          let targetH = height;

          if (width > maxSize || height > maxSize) {
            if (width >= height) {
              targetW = maxSize;
              targetH = Math.round((height / width) * maxSize);
            } else {
              targetH = maxSize;
              targetW = Math.round((width / height) * maxSize);
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, targetW, targetH);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = e.target!.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  imageCropped(event: ImageCroppedEvent) {
    if (event.blob) {
      this.selectedFile = new File([event.blob], 'profile.jpeg', {
        type: event.blob.type,
      });
    }
  }

  async saveCroppedImage() {
    const result = await this.imageCropperComponent?.crop('blob');
    if (result?.blob) {
      this.selectedFile = new File([result.blob], 'profile.jpeg', {
        type: result.blob.type,
      });
      clearTimeout(this.avatarLoadTimeout);
      this.avatarLoaded = true;
      this.croppedImage = this.sanitizer.bypassSecurityTrustUrl(
        URL.createObjectURL(result.blob),
      );
    }
    this.imageBase64ForCropper = '';
    this.zoom = 1;
    this.modal = false;
    this.toastService.show({
      color: 'success',
      header: 'Éxito',
      message: 'Imagen cargada correctamente',
    });
  }

  cancelCropperImage() {
    this.selectedFile = null;
    this.imageBase64ForCropper = '';
    this.zoom = 1;
    this.modal = false;
  }

  handleLiveDemoChange(event: any) {
    this.modal = event;
  }

  onCampaignImageLoad() {
    this.imageLoaded = true;
  }

  onCampaignImageError() {
    this.imageError = true;
    this.imageLoaded = true;
  }

  onAvatarLoad() {
    if (this.croppedImage) {
      clearTimeout(this.avatarLoadTimeout);
      this.avatarLoaded = true;
      return;
    }

    clearTimeout(this.avatarLoadTimeout);
    this.avatarLoadTimeout = setTimeout(() => {
      this.avatarLoaded = true;
    }, 1000);
  }

  onAvatarError() {
    clearTimeout(this.avatarLoadTimeout);
    this.avatarLoaded = true;
  }

  ngOnDestroy() {
    clearTimeout(this.avatarLoadTimeout);
  }

  createCredential() {
    if (!this.fullNameValue || this.fullNameValue.trim() === '') {
      this.toastService.show({
        color: 'danger',
        header: 'Error',
        message: 'Debes ingresar tu nombre',
      });
      return;
    }

    this.generate.emit({
      file: this.selectedFile ?? null,
      fullName: this.fullNameValue.trim(),
    });
  }
}
